import {
  Button,
  Menu,
  Popover,
  Positions,
  Select,
  TextInput,
  Variants
} from "@sede-x/shell-ds-react-framework";
import {
  Add,
  ChevronDown,
  TrashAltSolid
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import { useRef } from "react";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import { Picklists, TTicketInput, TVolumeInput } from "ticketing/ticketing.types";
import { isMotRailOrTruck } from "ticketing/utils";

const TemperatureSuffix = (
  data: Picklists.TTemperatureUOM[],
  value: Picklists.TTemperatureUOM | null,
  onChange: (value: Picklists.TTemperatureUOM, fieldName: keyof TVolumeInput) => void
): React.ReactNode => {
  const container = useRef<HTMLDivElement>(null);

  // Create menu items from temperature UOM data
  const menuItems = (data || []).map(item => ({
    label: item.shortName,
    key: item.id.toString(),
    onClick: () => {
      onChange(data.find(t => t.shortName === item.shortName)!, "temperatureUnitOfMeasure");
    }
  }));

  // Create the menu component
  const temperatureMenu = <Menu items={menuItems} />;

  return (
    <div ref={container} style={{ display: "inline-block" }}>
      <Popover
        popup={temperatureMenu}
        getPopupContainer={() => container.current as HTMLElement}
        trigger="click"
        placement="bottomRight">
        <Button
          variant={Variants.Transparent}
          icon={<ChevronDown />}
          iconPosition={Positions.Right}
          size="small"
          style={{ minWidth: "unset", padding: "0", margin: "0" }}>
          {value?.shortName ?? (data && data.length > 0 ? data[0].shortName : "C")}
        </Button>
      </Popover>
    </div>
  );
};

type TicketVolumeFormProps = {
  activeTicket: TTicketInput;
  volume: TVolumeInput;
  canDelete: boolean;
  onVolumeChange: (
    volume: TVolumeInput,
    change: {
      [key: string]: number | Picklists.TUnitOfMeasure | Picklists.TTemperatureUOM;
    }
  ) => void;
  onCopyVolume: (volume: TVolumeInput) => void;
  onDeleteVolume: (volume: TVolumeInput) => void;
};

export const TicketVolumeForm = ({
  activeTicket,
  volume,
  canDelete,
  onVolumeChange,
  onCopyVolume,
  onDeleteVolume
}: TicketVolumeFormProps) => {
  const { picklists } = usePicklists();

  const onInputChange = (
    value: number | Picklists.TTemperatureUOM | Picklists.TUnitOfMeasure,
    name: keyof TVolumeInput
  ) => {
    if (name === "grossVolume" || name === "netVolume") {
      value = Math.abs(value as number);
    }
    onVolumeChange(volume, { [name]: value });
  };

  const isGrossVolumeValid =
    !isMotRailOrTruck(activeTicket.modeOfTransport?.name) || !!volume.grossVolume;

  const uomClassFilter = volume.volumeType ? [volume.volumeType] : ["Volume", "Mass"];

  const unitOfMeasures = picklists?.unitOfMeasures?.filter(u =>
    uomClassFilter.includes(u.unitOfMeasureClass.name)
  );

  const disableAdd =
    !volume.unitOfMeasure ||
    !volume.temperature ||
    !volume.temperatureUnitOfMeasure ||
    !volume.netVolume;

  const inputClassName = "theme-form-input";

  return (
    <>
      <Select
        value={volume.unitOfMeasure?.name}
        fieldNames={{ label: "name", value: "id" }}
        optionLabelProp="name"
        //@ts-ignore
        options={unitOfMeasures}
        //@ts-ignore
        onChange={(_, value) => onInputChange(value as TUnitOfMeasure, "unitOfMeasure")}
        size={"small"}
        className={inputClassName}
        allowClear={false}
      />
      <TextInput
        id="temperature"
        name="temperature"
        disabled={volume.isDefault}
        value={volume?.temperature ?? undefined}
        onChange={value => onInputChange(+value.target.value, "temperature")}
        size={"small"}
        className={inputClassName}
        type="number"
        suffix={{
          node: TemperatureSuffix(
            picklists?.temperatureUOMs ?? [],
            volume.temperatureUnitOfMeasure ?? null,
            onInputChange
          ),
          transparent: true
        }}
      />
      <TextInput
        id="netVolume"
        name="netVolume"
        value={volume.netVolume}
        onInput={e => {
          const target = e.target as HTMLInputElement;
          if (target.value.includes(".")) {
            const [integer, decimal] = target.value.split(".");
            target.value =
              decimal.length > 7 ? `${integer}.${decimal.slice(0, 7)}` : target.value;
          }
        }}
        onChange={value => onInputChange(+value.target.value, "netVolume")}
        size={"small"}
        className={inputClassName}
        type="number"
      />

      <TextInput
        min={0}
        id="grossVolume"
        name="grossVolume"
        onInput={e => {
          const target = e.target as HTMLInputElement;
          if (target.value.includes(".")) {
            const [integer, decimal] = target.value.split(".");
            target.value =
              decimal.length > 7 ? `${integer}.${decimal.slice(0, 7)}` : target.value;
          }
        }}
        value={volume.grossVolume}
        onChange={value => onInputChange(+value.target.value, "grossVolume")}
        size={"small"}
        invalid={!isGrossVolumeValid}
        className={inputClassName}
        type="number"
      />

      <Button
        icon={<Add />}
        iconOnly={true}
        title={"Copy Volume"}
        sentiment="positive"
        variant="transparent"
        disabled={disableAdd}
        style={{ color: `${disableAdd ? "gray" : "green"}` }}
        onClick={() => onCopyVolume(volume)}></Button>
      {!volume.isDefault && (
        <Button
          icon={<TrashAltSolid />}
          iconOnly={true}
          variant="transparent"
          title={"Delete Volume"}
          sentiment="negative"
          onClick={() => onDeleteVolume(volume)}
          disabled={!canDelete}></Button>
      )}
      {volume.isDefault && <span></span>}
    </>
  );
};
